<div class="signup-page-wrapper">
  <div class="signup-container">
    @if (isLoading) {
      <app-skeleton type="signup-form"></app-skeleton>
    } @else {
      <div class="signup-header">
        <div class="logo-section">
          <img src="assets/acc-auth0.jpg" alt="App Logo" class="app-logo" />
        </div>
        <h1 class="signup-title">Create Your Account</h1>
        <p class="signup-subtitle">Join us to access all features</p>
      </div>

      <form
        [formGroup]="signupForm"
        (ngSubmit)="onSubmit()"
        class="signup-form"
        [class.form-disabled]="showSuccessMessage"
      >
        <!-- Success Message -->
        @if (showSuccessMessage) {
          <div class="success-message">
            <div class="success-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22,4 12,14.01 9,11.01" />
              </svg>
            </div>
            <div class="success-content">
              <h3>Success!</h3>
              <p>{{ successMessage }}</p>
              @if (isRedirecting) {
                <div class="redirect-indicator">
                  <svg
                    class="spinner"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="2"
                      fill="none"
                      stroke-dasharray="31.416"
                      stroke-dashoffset="31.416"
                    >
                      <animate
                        attributeName="stroke-dasharray"
                        dur="2s"
                        values="0 31.416;15.708 15.708;0 31.416"
                        repeatCount="indefinite"
                      />
                      <animate
                        attributeName="stroke-dashoffset"
                        dur="2s"
                        values="0;-15.708;-31.416"
                        repeatCount="indefinite"
                      />
                    </circle>
                  </svg>
                  <span>Redirecting...</span>
                </div>
              }
            </div>
          </div>
        }

        <!-- Error Message -->
        @if (invitationError && errorMessage) {
          <div class="error-banner">
            <div class="error-icon">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <p>{{ errorMessage }}</p>
          </div>
        } @else {
          <!-- Form Submission Error Message -->
          @if (errorMessage) {
            <div class="error-banner">
              <div class="error-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
              </div>
              <p>{{ errorMessage }}</p>
            </div>
          }

          <!-- Email Field (Read-only) -->
          <div class="form-group">
            <label for="email" class="form-label">Email Address</label>
            <div class="input-wrapper">
              <input
                id="email"
                type="email"
                class="form-input readonly"
                formControlName="email"
                placeholder="your@email.com"
                readonly
              />
              <div class="input-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-10 5L2 7" />
                </svg>
              </div>
            </div>
          </div>

          <!-- First Name -->
          <div class="form-group">
            <label for="firstName" class="form-label">First Name</label>
            <div class="input-wrapper">
              <input
                id="firstName"
                type="text"
                class="form-input"
                [class.error]="getFieldError('firstName')"
                formControlName="firstName"
                placeholder="Enter your first name"
              />
              <div class="input-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
            </div>
            @if (getFieldError("firstName")) {
              <div class="error-message">
                {{ getFieldError("firstName") }}
              </div>
            }
          </div>

          <!-- Last Name -->
          <div class="form-group">
            <label for="lastName" class="form-label">Last Name</label>
            <div class="input-wrapper">
              <input
                id="lastName"
                type="text"
                class="form-input"
                [class.error]="getFieldError('lastName')"
                formControlName="lastName"
                placeholder="Enter your last name"
              />
              <div class="input-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
            </div>
            @if (getFieldError("lastName")) {
              <div class="error-message">
                {{ getFieldError("lastName") }}
              </div>
            }
          </div>

          <!-- Password -->
          <div class="form-group">
            <label for="password" class="form-label">Password</label>
            <div class="input-wrapper">
              <input
                id="password"
                [type]="showPassword ? 'text' : 'password'"
                class="form-input"
                [class.error]="getFieldError('password')"
                formControlName="password"
                placeholder="Create a strong password"
              />
              <div class="input-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                  <path d="m7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <button
                type="button"
                class="password-toggle"
                (click)="togglePasswordVisibility('password')"
              >
                @if (!showPassword) {
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                }
                @if (showPassword) {
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24" />
                    <path
                      d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"
                    />
                    <path
                      d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"
                    />
                    <line x1="2" x2="22" y1="2" y2="22" />
                  </svg>
                }
              </button>
            </div>
            @if (
              signupForm.get("password")?.touched &&
              signupForm.get("password")?.invalid
            ) {
              <div class="password-requirements">
                <p>Password must contain:</p>
                <ul>
                  <li [class.valid]="hasLowercase()">One lowercase letter</li>
                  <li [class.valid]="hasUppercase()">One uppercase letter</li>
                  <li [class.valid]="hasNumber()">One number</li>
                  <li [class.valid]="hasSpecialChar()">
                    One special character
                  </li>
                  <li [class.valid]="hasMinLength()">At least 8 characters</li>
                </ul>
              </div>
            }
            @if (getFieldError("password")) {
              <div class="error-message">
                {{ getFieldError("password") }}
              </div>
            }
          </div>

          <!-- Confirm Password -->
          <div class="form-group">
            <label for="confirmPassword" class="form-label"
              >Confirm Password</label
            >
            <div class="input-wrapper">
              <input
                id="confirmPassword"
                [type]="showConfirmPassword ? 'text' : 'password'"
                class="form-input"
                [class.error]="getFieldError('confirmPassword')"
                formControlName="confirmPassword"
                placeholder="Confirm your password"
              />
              <div class="input-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                  <path d="m7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <button
                type="button"
                class="password-toggle"
                (click)="togglePasswordVisibility('confirmPassword')"
              >
                @if (!showConfirmPassword) {
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                }
                @if (showConfirmPassword) {
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="m9.88 9.88a3 3 0 1 0 4.24 4.24" />
                    <path
                      d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 11 8 11 8a13.16 13.16 0 0 1-1.67 2.68"
                    />
                    <path
                      d="M6.61 6.61A13.526 13.526 0 0 0 1 12s4 8 11 8a9.74 9.74 0 0 0 5.39-1.61"
                    />
                    <line x1="2" x2="22" y1="2" y2="22" />
                  </svg>
                }
              </button>
            </div>
            @if (getFieldError("confirmPassword")) {
              <div class="error-message">
                {{ getFieldError("confirmPassword") }}
              </div>
            }
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            class="submit-button"
            [disabled]="signupForm.invalid || isSubmitting"
          >
            @if (!isSubmitting) {
              <span>Create Account</span>
            }
            @if (isSubmitting) {
              <span class="loading-text">
                <svg class="spinner" width="20" height="20" viewBox="0 0 24 24">
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="2"
                    fill="none"
                    stroke-dasharray="31.416"
                    stroke-dashoffset="31.416"
                  >
                    <animate
                      attributeName="stroke-dasharray"
                      dur="2s"
                      values="0 31.416;15.708 15.708;0 31.416"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="stroke-dashoffset"
                      dur="2s"
                      values="0;-15.708;-31.416"
                      repeatCount="indefinite"
                    />
                  </circle>
                </svg>
                Creating Account...
              </span>
            }
          </button>
        }
      </form>

      <div class="signup-footer">
        <p>
          Already have an account?
          <a routerLink="/login" class="login-link">Sign in here</a>
        </p>
      </div>
    }
  </div>
</div>
